import { Button, Checkbox } from '@scholastic/mockingjay';
import { useId, useState, type FormEvent } from 'react';
import {
  type SourceSystem,
  type Class,
  useGetClassesApi,
  useProcessAutoEntitlementsApi,
} from 'eds-common/api/v3';
import { useGetMeApi } from 'eds-common/api/v2';
import { useAccessCodeRedemptionContext } from './AccessCodeRedemptionContext';

function getSelectAllState(
  selectedClassIds: string[],
  classes: Class[] = []
): boolean | 'indeterminate' {
  if (classes.length > 0) {
    if (selectedClassIds.length === classes.length) {
      return true;
    }
    if (selectedClassIds.length > 0) {
      return 'indeterminate';
    }
  }

  return false;
}

export function AutoEntitleClasses() {
  const { data: user } = useGetMeApi();
  const { goToNextStep, redemptionData, updateRedemptionData } =
    useAccessCodeRedemptionContext();

  const [selectedClassIds, setSelectedClassIds] = useState<string[]>([]);
  const classesLabelId = useId();

  const { data: classes = [], isLoading } = useGetClassesApi(
    {
      userId: user?.userId || '',
      orgId: user?.currentOrg?.orgId || '',
      source: (user?.userSource as SourceSystem) || '',
    },
    {
      enabled:
        user?.userSource === 'Ednition' ||
        user?.userSource === 'GoogleClassroom',
    }
  );

  const { mutate: processAutoEntitlements, isPending } =
    useProcessAutoEntitlementsApi({
      onError: (error) => {
        // TODO error messaging
        console.error('Error processing auto entitlements:', error);
      },
    });

  const handleSelectAll = (checked: boolean | 'indeterminate') => {
    if (checked === true || checked === 'indeterminate') {
      setSelectedClassIds(classes.map((cls) => cls.class_id));
    } else {
      setSelectedClassIds([]);
    }
  };

  const handleClassToggle =
    (classId: string) => (checked: boolean | 'indeterminate') => {
      if (checked === true) {
        setSelectedClassIds((prev) => [...prev, classId]);
      } else {
        setSelectedClassIds((prev) => prev.filter((id) => id !== classId));
      }
    };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!redemptionData.redeemedAccessCode || !user?.userId) {
      return;
    }

    if (selectedClassIds.length) {
      processAutoEntitlements({
        contextIds: selectedClassIds,
        contextType: 'Class',
        subscriptionId: redemptionData.redeemedAccessCode.subscriptionId,
        userId: user.userId,
      });
    }

    updateRedemptionData({ selectedClassIds });

    goToNextStep();
  };

  const hasClasses = classes.length > 0;

  if (!redemptionData.product || isLoading) {
    return null;
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-14">
      <div className="flex flex-col gap-6 text-lg text-neutral-900">
        {hasClasses && (
          <div>
            <p>
              Select which classes you want to have access to{' '}
              <strong className="font-bold">
                {redemptionData.product.productName}
              </strong>
              .
            </p>
            <p>You can also skip this step and set your classes up later.</p>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <p
            id={classesLabelId}
            className="text-sm font-semibold text-neutral-1300"
          >
            {hasClasses ? 'Classes' : 'Select classes'}
          </p>

          <div
            role="group"
            aria-labelledby={classesLabelId}
            className="flex flex-col gap-3"
          >
            {!hasClasses ? (
              <p className="mt-3 rounded-sm border border-dashed border-neutral-400 p-2 text-sm">
                You don&apos;t have any classes.{' '}
                {user?.userSource === 'GoogleClassroom'
                  ? 'After you finish setting up this product, you can import your Google Classroom classes to set up student access.'
                  : 'Manage student access to this product once your classes are available in the platform.'}
              </p>
            ) : (
              <>
                <Checkbox
                  label="Select all"
                  checked={getSelectAllState(selectedClassIds, classes)}
                  onCheckedChange={handleSelectAll}
                  disabled={isLoading || classes.length === 0}
                />

                <div className="ml-6 flex max-h-56 flex-col gap-3 overflow-y-auto">
                  {classes.map((cls) => (
                    <Checkbox
                      key={cls.class_id}
                      label={cls.name}
                      checked={selectedClassIds.includes(cls.class_id)}
                      onCheckedChange={handleClassToggle(cls.class_id)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-4">
        {(isLoading || classes.length > 0) && (
          <Button
            type="button"
            variant="secondary"
            onClick={() => goToNextStep()}
          >
            Skip
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          disabled={
            (selectedClassIds.length === 0 && classes.length > 0) || isPending
          }
        >
          Next
        </Button>
      </div>
    </form>
  );
}
